import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Fade,
  Divider,
  useMediaQuery,
  useTheme,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReactPlayer from 'react-player';
import { getCameraByIp, getVideoStreamUrl, isCameraOnline } from '../../utils/cameraService';

const VideoStreamDialog = ({ 
  open, 
  onClose, 
  event
}) => {
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [streamUrl, setStreamUrl] = useState(null);
  const [cameraInfo, setCameraInfo] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const playerRef = useRef(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));


  useEffect(() => {
    if (open && event) {
      
      setVideoLoading(true);
      setVideoError(false);
      setPlayerReady(false);
      setIsPlaying(true);
      
      // Get camera information using the service function
      const camera = getCameraByIp(event.cameraIp);
      setCameraInfo(camera);
      
      let url = null;
      
      if (camera) {
        if (camera.groupId) {
          url = getVideoStreamUrl(event.cameraIp, camera.groupId);
        }
        
        if (!url && event.groupId) {
          url = getVideoStreamUrl(event.cameraIp, event.groupId);
          
        }
        
        if (!url && event.originalMessage?.groupId) {
          url = getVideoStreamUrl(event.cameraIp, event.originalMessage.groupId);
        }
        
        if (!url) {
          console.warn('⚠️ All standard methods failed, checking camera mappings...');
          const allGroupIds = [1, 2, 3, 4, 5, 6, 7, 9, 10, 11, 12, 13, 14];
          for (const testGroupId of allGroupIds) {
            const testUrl = getVideoStreamUrl(event.cameraIp, testGroupId);
            if (testUrl) {
              url = testUrl;
              break;
            }
          }
        }
      }
      
      setStreamUrl(url);
      
      setIsRefreshing(false);
    }
  }, [open, event]);

  const handlePlayerReady = useCallback(() => {
    setVideoLoading(false);
    setVideoError(false);
    setPlayerReady(true);
    setIsRefreshing(false);
  }, []);

  const handlePlayerError = useCallback((error) => {
    console.error('❌ HLS Player error:', error);
    setVideoLoading(false);
    setVideoError(true);
    setPlayerReady(false);
    setIsRefreshing(false);
  }, []);

  const handlePlayerStart = useCallback(() => {
    setVideoLoading(false);
    setVideoError(false);
  }, []);

  const handlePlayerBuffer = useCallback(() => {
  }, []);

  const handlePlayerBufferEnd = useCallback(() => {
    setVideoLoading(false);
  }, []);

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    setVideoError(false);
    setPlayerReady(false);
    setVideoLoading(true);
    
    if (streamUrl) {
      const cacheBuster = `?t=${Date.now()}`;
      const urlWithCacheBuster = streamUrl.includes('?') 
        ? `${streamUrl}&t=${Date.now()}` 
        : `${streamUrl}${cacheBuster}`;
      
      setStreamUrl(null);
      setTimeout(() => {
        setStreamUrl(urlWithCacheBuster);
      }, 100);
    }
  }, [streamUrl]);

  const handleFullscreen = useCallback(() => {
    if (playerRef.current && playerRef.current.getInternalPlayer) {
      const internalPlayer = playerRef.current.getInternalPlayer();
      if (internalPlayer && internalPlayer.requestFullscreen) {
        internalPlayer.requestFullscreen();
      } else {
        const videoElement = document.querySelector('.react-player video');
        if (videoElement) {
          if (videoElement.requestFullscreen) {
            videoElement.requestFullscreen();
          } else if (videoElement.mozRequestFullScreen) {
            videoElement.mozRequestFullScreen();
          } else if (videoElement.webkitRequestFullscreen) {
            videoElement.webkitRequestFullscreen();
          } else if (videoElement.msRequestFullscreen) {
            videoElement.msRequestFullscreen();
          }
        }
      }
    }
  }, []);

  const handleClose = useCallback(() => {
    setVideoLoading(false);
    setVideoError(false);
    setStreamUrl(null);
    setCameraInfo(null);
    setIsRefreshing(false);
    setPlayerReady(false);
    setIsPlaying(false);
    
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  if (!event) return null;

  const isOnline = isCameraOnline(event.cameraIp);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isMobile ? "sm" : "xl"}
      fullWidth
      fullScreen={isSmallMobile}
      PaperProps={{
        sx: {
          bgcolor: 'linear-gradient(135deg, rgba(15, 15, 23, 0.98) 0%, rgba(25, 25, 35, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: isSmallMobile ? 0 : '16px',
          color: 'white',
          minHeight: isMobile ? '100vh' : '80vh',
          maxHeight: isMobile ? '100vh' : '95vh',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
          overflow: 'hidden',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          ...(isMobile && {
            m: 0,
            width: '100%',
            height: '100%'
          })
        }
      }}
      
      sx={{
        '& .MuiDialog-paper': {
          overflow: 'hidden'
        },
        '& .MuiDialogContent-root': {
          overflow: 'hidden',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none'
        }
      }}

      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: isMobile ? 1 : 2,
          px: isMobile ? 2 : 3,
          py: isMobile ? 2 : 3,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: 'linear-gradient(90deg, rgba(33, 150, 243, 0.1) 0%, rgba(76, 175, 80, 0.1) 100%)',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? 1 : 2,
          width: isMobile ? '100%' : 'auto',
          justifyContent: isMobile ? 'center' : 'flex-start'
        }}>
          <VideocamIcon sx={{ fontSize: isMobile ? 24 : 28, color: '#2196f3' }} />
          <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
            <Typography variant={isMobile ? "h6" : "h5"} component="div" sx={{ fontWeight: 600 }}>
              Live Stream
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255,255,255,0.7)', 
              mt: 0.5, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              justifyContent: isMobile ? 'center' : 'flex-start',
              fontSize: isMobile ? '0.75rem' : '0.875rem'
            }}>
              <LocationOnIcon sx={{ fontSize: 16 }} />
              {event.location || cameraInfo?.location || 'Unknown Location'}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255,255,255,0.6)', 
              mt: 0.5, 
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              Camera IP: {event.cameraIp}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          width: isMobile ? '100%' : 'auto',
          justifyContent: isMobile ? 'center' : 'flex-end'
        }}>
          <IconButton 
            onClick={handleFullscreen} 
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
              ...(isMobile && { minWidth: 44, minHeight: 44 })
            }}
            disabled={videoError || !playerReady}
            title="Fullscreen"
          >
            <FullscreenIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
          </IconButton>
          <IconButton 
            onClick={handleClose} 
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.2)' },
              ...(isMobile && { minWidth: 44, minHeight: 44 })
            }} 
            title="Close"
          >
            <CloseIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ 
        p: isMobile ? 1 : 3,
        overflow: 'hidden', 
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>      
        {/* Event Status Chips */}
        <Box sx={{ 
          mb: isMobile ? 2 : 3, 
          p: isMobile ? 1.5 : 2, 
          bgcolor: 'rgba(255,255,255,0.05)', 
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)'
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: isMobile ? 1 : 1.5,
            justifyContent: isMobile ? 'center' : 'flex-start'
          }}>
            <Chip 
              label={event.eventType || 'Unknown Event'} 
              size="small" 
              sx={{ 
                bgcolor: 'rgba(33, 150, 243, 0.9)', 
                color: 'white',
                fontWeight: 600,
                borderRadius: '8px',
                fontSize: isMobile ? '0.75rem' : '0.8125rem'
              }}
            />
            <Chip 
              label={event.area || cameraInfo?.location || 'Unknown Area'} 
              size="small" 
              sx={{ 
                bgcolor: 'rgba(76, 175, 80, 0.9)', 
                color: 'white',
                fontWeight: 600,
                borderRadius: '8px',
                fontSize: isMobile ? '0.75rem' : '0.8125rem'
              }}
            />
            {event.severity && (
              <Chip 
                label={`Priority: ${event.severity}`} 
                size="small" 
                sx={{ 
                  bgcolor: event.severity === 'High' || event.severity === 'high' ? '#f44336' : 
                           event.severity === 'Moderate' || event.severity === 'moderate' ? '#ff9800' : '#4caf50',
                  color: 'white',
                  fontWeight: 600,
                  borderRadius: '8px',
                  fontSize: isMobile ? '0.75rem' : '0.8125rem'
                }}
              />
            )}
            <Chip 
              label={`Camera: ${isOnline ? 'Online' : 'Offline'}`} 
              size="small" 
              sx={{ 
                bgcolor: isOnline ? 'rgba(76, 175, 80, 0.9)' : 'rgba(244, 67, 54, 0.9)',
                color: 'white',
                fontWeight: 600,
                borderRadius: '8px',
                fontSize: isMobile ? '0.75rem' : '0.8125rem'
              }}
            />
          </Box>
        </Box>

        {/* Video Stream Container */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: isMobile ? 'calc(100vh - 280px)' : '600px',
            minHeight: isMobile ? '300px' : '400px',
            bgcolor: 'rgba(0,0,0,0.9)',
            borderRadius: '12px',    
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            border: '2px solid rgba(255,255,255,0.1)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            flex: 1
          }}
        >
          {/* Small Refresh Button - Bottom Right Corner of Video */}
          <Tooltip title="Refresh Stream" placement="left">
            <IconButton 
              onClick={handleRefresh} 
              disabled={isRefreshing}
              sx={{ 
                position: 'absolute',
                bottom: 12,
                right: 12,
                zIndex: 10,
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                width: isMobile ? 28 : 32,
                height: isMobile ? 28 : 32,
                '&:hover': { 
                  bgcolor: 'rgba(33, 150, 243, 0.8)',
                  transform: 'scale(1.1)'
                },
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.2s ease-in-out',
                '&:disabled': {
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              <RefreshIcon sx={{ 
                fontSize: isMobile ? 14 : 16,
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
              }}} />
            </IconButton>
          </Tooltip>

          {!streamUrl ? (
            <Box sx={{ 
              textAlign: 'center', 
              px: isMobile ? 2 : 3,
              maxWidth: isMobile ? '90%' : '100%'
            }}>
              <Alert 
                severity="error" 
                sx={{ 
                  bgcolor: 'rgba(244, 67, 54, 0.15)', 
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  borderRadius: '8px',
                  color: 'white',
                  '& .MuiAlert-icon': { color: '#f44336' },
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}
              >
                Unable to generate stream URL. Camera may not be configured properly.
              </Alert>
            </Box>
          ) : (
            <>
              {/* Small Loading indicator - Top left corner */}
              <Fade in={isRefreshing || (videoLoading && !playerReady)}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'rgba(0,0,0,0.7)',
                    px: 1.5,
                    py: 1,
                    borderRadius: '8px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <CircularProgress sx={{ 
                    color: '#2196f3',
                    width: isMobile ? 16 : 18,
                    height: isMobile ? 16 : 18
                  }} />
                  {isRefreshing && (
                    <Typography variant="caption" sx={{ 
                      color: 'white',
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      fontWeight: 500
                    }}>
                      Refreshing...
                    </Typography>
                  )}
                </Box>
              </Fade>
              
              {/* React Player for HLS */}
              <ReactPlayer
                ref={playerRef}
                url={streamUrl}
                playing={isPlaying}
                muted={false}
                controls={true}
                width="100%"
                height="100%"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  borderRadius: '12px',
                }}
                config={{
                  file: {
                    attributes: {
                      crossOrigin: 'anonymous',
                      preload: 'auto'
                    },
                    forceHLS: true,
                    hlsOptions: {
                      enableWorker: true,
                      lowLatencyMode: true,
                      debug: false,
                      backBufferLength: 30,
                      maxBufferLength: 30,
                      maxMaxBufferLength: 30,
                      maxBufferSize: 30 * 1000 * 1000, // 30MB
                      maxBufferHole: 0.3,
                      highBufferWatchdogPeriod: 1,
                      nudgeOffset: 0.1,
                      nudgeMaxRetry: 5,
                      maxFragLookUpTolerance: 0.2,
                      liveSyncDurationCount: 3,
                      liveMaxLatencyDurationCount: 10,
                      enableSoftwareAES: false,
                      manifestLoadingTimeOut: 10000,
                      manifestLoadingMaxRetry: 2,
                      manifestLoadingRetryDelay: 500,
                      fragLoadingTimeOut: 10000,
                      fragLoadingMaxRetry: 4,
                      fragLoadingRetryDelay: 500,
                      startFragPrefetch: true,
                      testBandwidth: true,
                      progressive: false,
                      lowLatencyMode: true
                    }
                  }
                }}
                onReady={handlePlayerReady}
                onStart={handlePlayerStart}
                onError={handlePlayerError}
                onBuffer={handlePlayerBuffer}
                onBufferEnd={handlePlayerBufferEnd}
                onProgress={(progress) => {
                }}
                onDuration={(duration) => {
                }}
              />
              
              {/* Error Display */}
              {videoError && (
                <Fade in={videoError}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    zIndex: 3,
                    backgroundColor: 'rgba(0,0,0,0.9)',
                    p: isMobile ? 2 : 3,
                    borderRadius: '12px',
                    border: '1px solid rgba(244, 67, 54, 0.3)',
                    maxWidth: isMobile ? '90%' : '100%'
                  }}>
                    <Alert 
                      severity="error" 
                      sx={{ 
                        bgcolor: 'rgba(244, 67, 54, 0.15)', 
                        mb: 2,
                        color: 'white',
                        '& .MuiAlert-icon': { color: '#f44336' },
                        fontSize: isMobile ? '0.875rem' : '1rem'
                      }}
                    >
                      Failed to load video stream. Please check the stream URL or try refreshing.
                    </Alert>
                    <Button 
                      onClick={handleRefresh} 
                      variant="contained"
                      size={isMobile ? "small" : "medium"}
                      sx={{ 
                        bgcolor: '#2196f3',
                        '&:hover': { bgcolor: '#1976d2' }
                      }}
                    >
                      Try Again
                    </Button>
                  </Box>
                </Fade>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        borderTop: '1px solid rgba(255,255,255,0.1)',
        p: isMobile ? 2 : 3,
        gap: isMobile ? 1 : 2,
        justifyContent: 'center'
      }}>
        <Button 
          onClick={handleRefresh} 
          variant="outlined"
          size={isMobile ? "small" : "medium"}
          sx={{ 
            color: 'white',
            borderColor: 'rgba(255,255,255,0.3)',
            '&:hover': { 
              borderColor: '#2196f3',
              bgcolor: 'rgba(33, 150, 243, 0.1)'
            },
            fontSize: isMobile ? '0.875rem' : '1rem'
          }}
          disabled={isRefreshing}
        >
          Refresh Stream
        </Button>
        <Button 
          onClick={handleClose} 
          variant="contained"
          size={isMobile ? "small" : "medium"}
          sx={{ 
            bgcolor: '#2196f3',
            '&:hover': { bgcolor: '#1976d2' },
            fontSize: isMobile ? '0.875rem' : '1rem'  
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoStreamDialog;